import { useState, useMemo, useEffect } from "react";
import { CalendarIcon, ClockIcon } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MaskBadge } from "@/components/ui/mask-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { calculateDurationBetweenDates } from "@/utils/timeFormatting";
import { PlanningMeasurementRequest } from "@/types/planning";

type SelectedWaferInfo = {
  requestId: number;
  waferId: number;
  waferName: string;
  requestName: string;
  maskName: string;
  testLabelName: string;
  testLabelId: number;
  measurementSystemName: string | null;
  dieCount: number;
};

type WaferTimeInfo = {
  startedAt: string;
  completedAt: string;
};

type MarkWafersAsMeasuredDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedWaferKeys: Set<string>;
  measurementRequests: PlanningMeasurementRequest[];
  onConfirm: (waferTimes: Record<string, WaferTimeInfo>) => Promise<void>;
  isLoading?: boolean;
};

export function MarkWafersAsMeasuredDialog({
  isOpen,
  onOpenChange,
  selectedWaferKeys,
  measurementRequests,
  onConfirm,
  isLoading = false,
}: MarkWafersAsMeasuredDialogProps) {
  const navigate = useNavigate();
  // Track times for each wafer individually
  const [waferTimes, setWaferTimes] = useState<Record<string, WaferTimeInfo>>({});

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const nowString = now.toISOString(); // Format for datetime-local input
      // Set default times for all selected wafers
      const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
      const defaultTimes: Record<string, WaferTimeInfo> = {};

      selectedWaferKeys.forEach((key) => {
        defaultTimes[key] = {
          startedAt: nowString,
          completedAt: oneHourLater.toISOString(),
        };
      });

      setWaferTimes(defaultTimes);
    } else {
      setWaferTimes({});
    }
  }, [isOpen, selectedWaferKeys]);

  // Extract wafer information from selected keys and measurement requests
  const selectedWafers = useMemo((): SelectedWaferInfo[] => {
    const wafers: SelectedWaferInfo[] = [];

    selectedWaferKeys.forEach((key) => {
      const [reqId, waferId] = key.split("-");
      const requestId = Number(reqId);
      const waferIdNum = Number(waferId);

      const request = measurementRequests.find((req) => req.id === requestId);
      if (!request) {
        return;
      }

      let waferFound = false;
      request.waferRequests.forEach((wr) => {
        const match = wr._id === waferIdNum;
        if (match) {
          waferFound = true;
          wafers.push({
            requestId: request.id,
            waferId: wr._id,
            waferName: wr.name,
            requestName: request.name,
            maskName: request.mask.name,
            testLabelName: request.testLabel.name,
            testLabelId: request.testLabel.id,
            measurementSystemName: wr.measurementSystem?.name || null,
            dieCount: wr.dieCount,
          });
        }
      });

      if (!waferFound) {
        console.warn(`Wafer not found for ID: ${waferIdNum} in request ${requestId}`);
      }
    });

    return wafers;
  }, [selectedWaferKeys, measurementRequests]);

  // Helper function to check if wafer times are valid
  const isWaferTimeValid = (waferKey: string) => {
    const times = waferTimes[waferKey];
    if (!times || !times.startedAt || !times.completedAt) return false;

    const start = new Date(times.startedAt);
    const end = new Date(times.completedAt);

    return end.getTime() > start.getTime();
  };

  // Update wafer time
  const updateWaferTime = (waferKey: string, field: "startedAt" | "completedAt", value: string) => {
    setWaferTimes((prev) => ({
      ...prev,
      [waferKey]: {
        ...prev[waferKey],
        [field]: value,
      },
    }));
  };

  // Group wafers by measurement request (each request has only one mask)
  const wafersByRequest = useMemo(() => {
    const requestGroups = new Map<
      number,
      {
        requestId: number;
        requestName: string;
        maskName: string;
        wafers: SelectedWaferInfo[];
      }
    >();

    selectedWafers.forEach((wafer) => {
      const existingRequest = requestGroups.get(wafer.requestId);

      if (existingRequest) {
        existingRequest.wafers.push(wafer);
      } else {
        requestGroups.set(wafer.requestId, {
          requestId: wafer.requestId,
          requestName: wafer.requestName,
          maskName: wafer.maskName,
          wafers: [wafer],
        });
      }
    });

    return requestGroups;
  }, [selectedWafers]);

  const handleConfirm = async () => {
    // Validate all wafer times
    const invalidWafers = selectedWafers.filter((wafer) => {
      const waferKey = `${wafer.requestId}-${wafer.waferId}`;
      return !isWaferTimeValid(waferKey);
    });

    if (invalidWafers.length > 0) {
      toast.error(`Please set valid start and completion times for all wafers`);
      return;
    }

    try {
      await onConfirm(waferTimes);
    } catch (error) {
      console.error("Error confirming measurement completion:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[70vw] sm:max-w-[70vw] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5" />
            Mark Wafers as Measured
          </DialogTitle>
          <DialogDescription>
            Set individual start and completion times for {selectedWafers.length} wafer
            {selectedWafers.length !== 1 ? "s" : ""} across {wafersByRequest.size} measurement
            request
            {wafersByRequest.size !== 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {/* Selected Wafers with Individual Time Controls */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Wafer Measurement Times</h3>

            <div className="grid gap-2">
              {Array.from(wafersByRequest.entries()).map(([requestId, requestData]) => (
                <Card key={requestId} className="border bg-gray-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span className="text-lg font-semibold">{requestData.requestName}</span>
                      <MaskBadge maskName={requestData.maskName} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="text-sm text-muted-foreground">
                          {requestData.wafers.length} wafer
                          {requestData.wafers.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {requestData.wafers.map((wafer) => {
                          const waferKey = `${wafer.requestId}-${wafer.waferId}`;
                          const wt = waferTimes[waferKey];
                          const isValid = isWaferTimeValid(waferKey);
                          const duration = wt
                            ? calculateDurationBetweenDates(wt.startedAt, wt.completedAt)
                            : null;

                          return (
                            <Card key={waferKey} className="border">
                              <CardContent className="p-3">
                                <div className="space-y-3">
                                  {/* Wafer Info Header */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                      <span className="text-sm font-medium">{wafer.waferName}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                      <Badge variant="outline" className="text-xs">
                                        {wafer.measurementSystemName || "Unassigned"}
                                      </Badge>
                                      <span className="text-muted-foreground">
                                        Dies: {wafer.dieCount}
                                      </span>
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                                        onClick={() =>
                                          navigate(`/test-labels/${wafer.testLabelId}`)
                                        }
                                      >
                                        {wafer.testLabelName}
                                      </button>
                                    </div>
                                  </div>
                                  {/* Time Controls with Duration */}
                                  <div className="grid grid-cols-[1fr_1fr_200px] gap-6">
                                    <div className="space-y-2">
                                      <Label className="flex items-center gap-2 text-xs">
                                        <CalendarIcon className="h-3 w-3" />
                                        Start Time
                                      </Label>
                                      <Input
                                        type="datetime-local"
                                        value={wt?.startedAt || ""}
                                        onChange={(e) =>
                                          updateWaferTime(waferKey, "startedAt", e.target.value)
                                        }
                                        className="text-xs"
                                        max={wt?.completedAt || undefined}
                                      />
                                    </div>

                                    <div className="space-y-2">
                                      <Label className="flex items-center gap-2 text-xs">
                                        <CalendarIcon className="h-3 w-3" />
                                        Completion Time
                                      </Label>
                                      <Input
                                        type="datetime-local"
                                        value={wt?.completedAt || ""}
                                        onChange={(e) =>
                                          updateWaferTime(waferKey, "completedAt", e.target.value)
                                        }
                                        className="text-xs"
                                        min={wt?.startedAt || undefined}
                                        disabled={!wt?.startedAt}
                                      />
                                    </div>

                                    <div className="space-y-2">
                                      <Label className="flex items-center gap-2 text-xs">
                                        <ClockIcon className="h-3 w-3" />
                                        Duration
                                      </Label>
                                      <div className="h-9 flex items-center px-3 rounded-md border bg-muted">
                                        <span
                                          className={`text-xs font-medium ${isValid ? "text-green-600" : "text-muted-foreground"}`}
                                        >
                                          {duration || "—"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>{" "}
                                  {/* Validation Messages */}
                                  {wt?.startedAt && wt?.completedAt && !isValid && (
                                    <p className="text-xs text-red-500">
                                      Completion time must be after start time
                                    </p>
                                  )}
                                  {!wt?.startedAt && (
                                    <p className="text-xs text-muted-foreground">
                                      Please set start time
                                    </p>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              selectedWafers.some((wafer) => {
                const waferKey = `${wafer.requestId}-${wafer.waferId}`;
                return !isWaferTimeValid(waferKey);
              }) || isLoading
            }
          >
            {isLoading ? "Processing..." : "Mark as Measured"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
